/**
 * Brand Selector Component
 * 
 * Allows users to select which brand's pipeline configuration to view.
 * Default is Changan (Pilot brand).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PipelineBrand } from '../../types';

interface BrandSelectorProps {
  selectedBrand: PipelineBrand;
  onBrandChange: (brand: PipelineBrand) => void;
  className?: string;
}

const brands: { id: PipelineBrand; name: string; nameAr: string; isPilot: boolean }[] = [
  { id: 'Changan', name: 'Changan', nameAr: 'شانجان', isPilot: true },
  { id: 'Hyundai', name: 'Hyundai', nameAr: 'هيونداي', isPilot: false },
  { id: 'Stellantis', name: 'Stellantis', nameAr: 'ستيلانتس', isPilot: false },
];

export const BrandSelector: React.FC<BrandSelectorProps> = ({
  selectedBrand,
  onBrandChange,
  className = '',
}) => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
        {isArabic ? 'العلامة التجارية:' : 'Brand:'}
      </label>
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        {brands.map((brand) => (
          <button
            key={brand.id}
            onClick={() => onBrandChange(brand.id)}
            className={`
              px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200
              ${selectedBrand === brand.id
                ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }
            `}
          >
            <span className="flex items-center gap-1.5">
              {isArabic ? brand.nameAr : brand.name}
              {brand.isPilot && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                  {isArabic ? 'تجريبي' : 'Pilot'}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default BrandSelector;
